// Percorso file: src/components/AuthAvatar.jsx
//
// Avatar che scarica la foto profilo dal BACKEND con fetch autenticata.
//
// PERCHE'
// Lo Storage e' chiuso in lettura (storage.rules, opzione A): gli URL
// pubblici "firebasestorage.googleapis.com/..." composti a mano non
// funzionano piu'. Un tag <img> non puo' allegare l'header Authorization,
// quindi la foto si scarica via fetch da GET /users/:id/photo, si
// trasforma in blob e si mostra con un object URL temporaneo.
//
// CACHE
// Una cache in memoria condivisa fra tutte le istanze (Map a livello di
// modulo) evita di riscaricare la stessa foto quando compare piu' volte
// nella stessa pagina (liste utenti, chat). La voce di cache tiene un
// conteggio dei riferimenti: quando l'ultimo componente che la usa viene
// smontato, l'object URL viene revocato e la voce eliminata — niente
// perdite di memoria e, al rientro nella pagina, dati freschi.
// Anche l'ESITO NEGATIVO (nessuna foto / errore) viene messo in cache,
// cosi' la stessa foto mancante non viene richiesta due volte.
//
// QUANDO NON SI CHIAMA IL BACKEND
// Se la prop photoKey e' vuota, l'utente non ha foto: si mostra subito il
// ripiego senza alcuna richiesta. Su liste con decine di soci senza foto
// questo evita altrettante richieste destinate a rispondere 404.

import React, { useEffect, useState } from "react";
import { API_ROOT, authHeaders } from "../api/api";

// userId -> { riferimenti, objectUrl, promessa }
const cacheFoto = new Map();

// Scarica la foto e restituisce un object URL, oppure null se non c'e'
// (404) o se la richiesta fallisce.
async function scaricaFoto(chiaveUtente) {
  try {
    const res = await fetch(`${API_ROOT}/users/${encodeURIComponent(chiaveUtente)}/photo`, {
      credentials: "include",
      headers: await authHeaders(),
    });

    // 404 = nessuna foto caricata: e' un caso normale, non un errore.
    if (!res.ok) return null;

    const blob = await res.blob();
    if (!blob || blob.size === 0) return null;

    return URL.createObjectURL(blob);
  } catch (err) {
    console.warn("[AuthAvatar] foto non disponibile:", chiaveUtente, err?.message || err);
    return null;
  }
}

// Prende (o crea) la voce di cache e ne incrementa i riferimenti.
function acquisisci(chiave, idUtente) {
  let voce = cacheFoto.get(chiave);

  if (!voce) {
    voce = { riferimenti: 0, objectUrl: null, promessa: null };
    cacheFoto.set(chiave, voce);

    voce.promessa = scaricaFoto(idUtente).then((url) => {
      // Se nel frattempo l'ultimo utilizzatore e' sparito, l'object URL
      // va revocato subito: nessuno lo mostrera' mai.
      if (voce.riferimenti <= 0) {
        if (url) URL.revokeObjectURL(url);
        // Solo se la voce in cache e' ancora QUESTA: con un rimontaggio
        // rapido (o StrictMode) la mappa puo' gia' contenere una voce
        // nuova con la stessa chiave, e cancellarla la renderebbe
        // orfana — il suo object URL non verrebbe mai revocato.
        if (cacheFoto.get(chiave) === voce) cacheFoto.delete(chiave);
        return null;
      }
      voce.objectUrl = url;
      return url;
    });
  }

  voce.riferimenti += 1;
  return voce;
}

// Decrementa i riferimenti; all'ultimo revoca l'object URL.
function rilascia(chiave) {
  const voce = cacheFoto.get(chiave);
  if (!voce) return;

  voce.riferimenti -= 1;

  if (voce.riferimenti <= 0) {
    if (voce.objectUrl) URL.revokeObjectURL(voce.objectUrl);
    // Come sopra: si cancella solo la voce che si sta effettivamente
    // rilasciando, mai una omonima piu' recente.
    if (cacheFoto.get(chiave) === voce) cacheFoto.delete(chiave);
  }
}

// Iniziali di ripiego, quando non c'e' foto ne' immagine segnaposto.
function iniziali(nome) {
  const parti = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (parti.length === 0) return "";
  if (parti.length === 1) return parti[0].charAt(0).toUpperCase();
  return (parti[0].charAt(0) + parti[parti.length - 1].charAt(0)).toUpperCase();
}

/**
 * @param {string|number} userId    id dell'utente (documento Firestore)
 * @param {string} name            nome completo, usato per le iniziali di ripiego
 * @param {string} fallbackSrc     immagine segnaposto (facoltativa)
 * @param {React.ReactNode} fallback  ripiego personalizzato (vince su tutto)
 * @param {string} photoKey        riferimento alla foto dell'utente
 *                                 (es. user.photoUrl). Se cambia, forza un
 *                                 nuovo scaricamento; SE E' VUOTO l'utente
 *                                 non ha foto e non si chiama il backend.
 * Le altre prop (className, style, alt, onClick, title) sono passate
 * all'elemento renderizzato, cosi' il CSS delle pagine resta invariato.
 */
export default function AuthAvatar({
  userId,
  name = "",
  alt = "avatar",
  className = "",
  style,
  fallbackSrc = null,
  fallback = null,
  photoKey = "",
  onClick,
  title,
}) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    setSrc(null);

    // Nessun utente, oppure utente senza foto: si va diretti al ripiego
    // senza interrogare il backend. Su una lista di 90 soci senza foto
    // questo evita 90 richieste destinate a rispondere 404.
    if (!userId || !photoKey) return undefined;

    const chiave = `${userId}|${photoKey}`;
    let vivo = true;

    const voce = acquisisci(chiave, userId);

    voce.promessa
      .then((url) => {
        if (vivo && url) setSrc(url);
      })
      .catch(() => {
        /* gia' gestito in scaricaFoto: si resta sul ripiego */
      });

    return () => {
      vivo = false;
      rilascia(chiave);
    };
  }, [userId, photoKey]);

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        title={title}
      />
    );
  }

  // --- ripieghi, in ordine di precedenza ---

  if (fallback) {
    return fallback;
  }

  if (fallbackSrc) {
    return (
      <img
        src={fallbackSrc}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        title={title}
      />
    );
  }

  const sigla = iniziali(name);

  return (
    <span
      className={`auth-avatar-fallback ${className}`.trim()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // Valori dal tema unico (src/theme.css, doc 07 §10.1): superficie
        // e oro QPSC. Sostituiscono #2a2a2a e #d4b06a, aboliti dal §1.4.
        background: "var(--qpsc-surface)",
        color: "var(--qpsc-gold)",
        fontWeight: 600,
        ...style,
      }}
      onClick={onClick}
      title={title}
      aria-label={alt}
    >
      {sigla || "?"}
    </span>
  );
}
